import { useCallback, useEffect, useRef, useState } from 'react'
import { Application, Rectangle, Sprite, Texture } from 'pixi.js'
import { CATEGORY_SETUP, useEditor } from '../store/useEditor'
import { isDecoration, isStrand, type Point, type Strand } from '../types/document'
import { buildEdgeMap, snapToEdge, traceRun } from '../lib/edges'
import {
  detectRoof,
  facetForStroke,
  facetNear,
  facetThroughStroke,
  type RoofFacet,
} from '../lib/roof'
import { accessoryStyles } from '../lib/decorLibrary'
import { suggestNight } from '../lib/nightGrade'
import { NightRenderer } from './night/NightRenderer'
import { setAssetReadyCallback } from './assets'
import { distanceToPath, toPx } from '../lib/geometry'
import { proposalPick, selectedProposalGrab } from '../lib/grab'
import {
  createSceneRefs,
  drawOverlay,
  setBulbLook,
  BULB,
  layoutBranding,
  decorationSize,
  renderLights,
  type RenderStats,
  type SceneRefs,
} from './scene'

interface View {
  zoom: number
  x: number
  y: number
}

export interface StageHandle {
  exportBlob: (scale?: number) => Promise<Blob | null>
  fit: () => void
  zoomBy: (factor: number) => void
  /**
   * The night renderer, for the developer tuning panel.
   *
   * Handed out rather than mirrored into React state on purpose: these are
   * shader uniforms, and a tuning drag has to be a float write and a frame,
   * not a store update and a re-render.
   */
  night: () => NightRenderer | null
}

export interface SelectionBox {
  midX: number
  midY: number
  top: number
  bottom: number
}

interface Props {
  onStats: (s: RenderStats) => void
  onReady: (h: StageHandle) => void
  onZoomChange?: (zoom: number) => void
  /** Where the selected run sits in the stage, so a badge can follow it. */
  onSelectionBox?: (b: SelectionBox | null) => void
}

const DRAG_SLOP = 6

/**
 * How close a click has to be to count as hitting a run.
 *
 * A bare tolerance in screen pixels is right for a roofline, which really is a
 * line, and wrong for everything with body. Garland is seventy pixels thick on
 * a typical photo, a wrapped trunk is as wide as the trunk, and a cluster is
 * two rails apart - so clicking the greenery you can plainly see landed forty
 * pixels from the path underneath it and hit nothing at all. It looked exactly
 * like the garland could not be selected.
 *
 * So the target is whichever is larger: a comfortable fingertip radius, or the
 * run's own half-width. You can hit what you can see.
 */
function hitTolerance(l: Strand, photoWidth: number, zoom: number): number {
  const base = 16 / zoom
  if (l.kind === 'swag' || l.kind === 'wrap' || l.kind === 'cluster') {
    return Math.max(base, photoWidth * l.wrapWidth * 0.62)
  }
  if (l.kind === 'freeform') {
    // A bush fills an area rather than following a line.
    return Math.max(base, photoWidth * l.wrapWidth * 1.5)
  }
  /*
   * A frond is a line, so it is grabbed like one - but a palm crown is a dozen
   * of them crossing each other, and the default tolerance makes picking the
   * one you meant a game. Tighter than a bush, looser than nothing.
   */
  if (l.kind === 'frond') return base * 0.7
  return base
}


export default function StageView({ onStats, onReady, onZoomChange, onSelectionBox }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const appRef = useRef<Application | null>(null)
  const refsRef = useRef<SceneRefs | null>(null)
  const photoSpriteRef = useRef<Sprite | null>(null)
  const viewRef = useRef<View>({ zoom: 1, x: 0, y: 0 })
  const downRef = useRef<{
    x: number
    y: number
    world: Point | null
    moved: boolean
    /** When the press started, so a flicked tap can be told from a real one. */
    t: number
  } | null>(null)
  /** Ignore taps for a moment after a sweep, so lifting off does not re-arm. */
  const quietUntilRef = useRef(0)
  const dragPointRef = useRef<{ strandId: string; index: number } | null>(null)
  /**
   * Dragging a circle on a PROPOSED run, which owns no Strand yet.
   *
   * Edit on the run card arms this. A confirmed run is dragged through its
   * Strand as any run is, and the store turns that into a proposal edit, which
   * is what withdraws the confirmation.
   */
  const dragProposalRef = useRef<{ id: string; index: number } | null>(null)
  /** Moving a whole run that a proposal owns, proposed or confirmed. */
  const dragProposalRunRef = useRef<{ id: string; from: Point; points: Point[] } | null>(null)
  const dragDecorRef = useRef<{ id: string; dx: number; dy: number } | null>(null)
  const freehandRef = useRef<Point | null>(null)
  /** A press-and-drag on a run tool, before we know if it is a tap or a stroke. */
  const sweepRef = useRef<{ from: Point; active: boolean } | null>(null)
  /** Dragging a whole run to reposition it, rather than one of its corners. */
  const dragRunRef = useRef<{
    id: string
    from: Point
    points: Point[]
    /**
     * True until the pointer has actually traveled.
     *
     * A press on a finished run is ambiguous: it might mean "pick this up" or
     * it might mean "start a new run here", and on a roof the second is common -
     * a gable crosses an eave, garland runs over a roofline. Committing to the
     * move on pointerdown made it impossible to draw anywhere near something
     * already drawn, which is a worse problem than the one it solved.
     *
     * So the press only becomes a move once the pointer moves. Released
     * without traveling, it falls through and draws exactly as if the run were
     * not there. Drag to move, tap to draw - unambiguous, and it works the same
     * with a finger.
     */
    pending: boolean
  } | null>(null)
  /** Pulling a corner or a side handle of the selected decoration. */
  const resizeRef = useRef<{
    id: string
    corner: boolean
    /** The fixed point the drag scales away from, in photo pixels. */
    ax: number
    ay: number
    scale0: number
    scaleY0: number | undefined
    w0: number
    h0: number
  } | null>(null)
  const panRef = useRef<{ x: number; y: number; vx: number; vy: number } | null>(null)
  /** Space is down, so a drag moves the photo instead of drawing on it. */
  const spaceRef = useRef(false)
  const lastDocRef = useRef<unknown>(null)
  const lastStatsRef = useRef<RenderStats>({ totalBulbs: 0, totalFeet: null, perStrand: [] })
  const lastGradeRef = useRef<string>('')
  const lastDraftRef = useRef<string>('')
  const brandSpriteRef = useRef<Sprite | null>(null)
  const lastLogoRef = useRef<string | null>(null)
  const sourceImgRef = useRef<HTMLImageElement | null>(null)
  const nightRef = useRef<NightRenderer | null>(null)
  const roofRef = useRef<RoofFacet[] | null>(null)

/*
 * The dial used to be squared, because night was a cross-fade back to the
 * photograph as shot and the bottom of the track was useless. It is not a
 * cross-fade any more - every position is a real grade - so the control is
 * linear and Night Intensity means what it says: 75 is three quarters of the
 * blue hour treatment.
 */

  /**
   * The night grade, on the GPU, attached to the photograph and nothing else.
   *
   * This replaces a CPU pass that rebuilt a full graded bitmap every time the
   * dial moved and took the better part of three seconds to do it. Now the
   * photo is uploaded once, every parameter is a uniform, and moving the dial
   * is a handful of float writes.
   */
  const night = useCallback((): NightRenderer => {
    if (!nightRef.current) nightRef.current = new NightRenderer()
    return nightRef.current
  }, [])

  /** Screen pixels to normalized photo coordinates. */
  const toDoc = useCallback((clientX: number, clientY: number): Point | null => {
    const app = appRef.current
    const refs = refsRef.current
    const photo = useEditor.getState().doc.photo
    if (!app || !refs || !photo) return null
    const rect = app.canvas.getBoundingClientRect()
    const sx = clientX - rect.left
    const sy = clientY - rect.top
    const v = viewRef.current
    return {
      x: (sx - v.x) / v.zoom / photo.width,
      y: (sy - v.y) / v.zoom / photo.height,
    }
  }, [])

  /**
   * THE PHOTOGRAPH CANNOT LEAVE THE FRAME.
   *
   * Reported as: I moved it and it almost became a black screen because the
   * picture was so out of the frame. Panning was unbounded, so a scroll on a
   * fitted photo pushed it off the edge and left somebody looking at nothing,
   * with no clue that the house was two thousand pixels to the left.
   *
   * Removing panning was the other option and it is the wrong one: at 194%,
   * where you go to put an endpoint exactly on a corner, there is no way to
   * reach the rest of the house without moving it. The bug was never that the
   * photo moves. It was that nothing stopped it.
   *
   * So: when the photo is SMALLER than the view it is pinned centered and does
   * not move at all, which is the fitted case and the one that produced the
   * black screen. When it is LARGER it may move, and stops when an edge of the
   * photograph reaches the matching edge of the view. Applied in one place, so
   * every route in - scroll, space drag, the Move tool, zooming about the
   * cursor - is bounded by the same rule.
   */
  const clampView = useCallback(() => {
    const app = appRef.current
    const photo = useEditor.getState().doc.photo
    if (!app || !photo) return
    const host = hostRef.current
    const cw = host?.clientWidth || app.renderer.width
    const ch = host?.clientHeight || app.renderer.height
    const v = viewRef.current
    const w = photo.width * v.zoom
    const h = photo.height * v.zoom

    v.x = w <= cw ? (cw - w) / 2 : Math.min(0, Math.max(cw - w, v.x))
    v.y = h <= ch ? (ch - h) / 2 : Math.min(0, Math.max(ch - h, v.y))
  }, [])

  const applyView = useCallback(() => {
    const refs = refsRef.current
    if (!refs) return
    clampView()
    const v = viewRef.current
    refs.world.position.set(v.x, v.y)
    refs.world.scale.set(v.zoom)
  }, [clampView])

  const fit = useCallback(() => {
    const app = appRef.current
    const photo = useEditor.getState().doc.photo
    if (!app || !photo) return
    // Measure the element, not the renderer. The renderer's own size can lag a
    // layout change (opening the panel, rotating a tablet) by a frame, which is
    // what made the photo open smaller than the space available for it.
    const host = hostRef.current
    const cw = host?.clientWidth || app.renderer.width
    const ch = host?.clientHeight || app.renderer.height
    if (host && (app.renderer.width !== cw || app.renderer.height !== ch)) {
      app.renderer.resize(cw, ch)
    }
    // Barely any padding. The photo is the whole point, so it should open as
    // large as the window allows rather than floating in a sea of background.
    const pad = 8
    const zoom = Math.min((cw - pad * 2) / photo.width, (ch - pad * 2) / photo.height)
    viewRef.current = {
      zoom,
      x: (cw - photo.width * zoom) / 2,
      y: (ch - photo.height * zoom) / 2,
    }
    applyView()
    onZoomChange?.(zoom)
    redraw()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyView, onZoomChange])

  const redrawRef = useRef<(() => void) | null>(null)
  const redraw = useCallback(() => {
    const refs = refsRef.current
    if (!refs) return
    const st = useEditor.getState()
    const photo = st.doc.photo
    if (!photo) {
      onStats({ totalBulbs: 0, totalFeet: null, perStrand: [] })
      return
    }

    /**
     * Rebuilding every bulb sprite on every store change would stutter while
     * the cursor moves during a draw. Document mutations always produce a new
     * object, so a reference check tells us when the lights actually changed.
     * Overlay work is cheap and always runs.
     */
    /*
     * ONE CLICK DRAWS NOTHING.
     *
     * A single tap set an anchor, and because the cursor position was appended
     * to it the draft already had two points - so a full run of bulbs was
     * rendered along a line the user had not finished, and every stray click on
     * the photo left lights behind.
     *
     * A run needs two REAL points before any light exists. Until then the
     * overlay shows a rubber band and nothing else, which is the honest
     * feedback: here is where it would start, and nothing is committed yet.
     */
    const draftPts =
      st.draft && st.draft.length >= 2
        ? st.hover
          ? [...st.draft, st.hover]
          : st.draft
        : null
    const draftKey = draftPts ? JSON.stringify(draftPts) + JSON.stringify(st.defaults) : ''

    const docChanged = lastDocRef.current !== st.doc || draftKey !== lastDraftRef.current
    if (docChanged) {
      lastDocRef.current = st.doc
      lastDraftRef.current = draftKey
      const d = st.defaults
      lastStatsRef.current = renderLights(
        refs,
        st.doc,
        draftPts && draftPts.length >= 2
          ? {
              points: draftPts,
              product: d.product,
              palette: d.palette,
              patternRepeat: d.patternRepeat,
              bulbScale: d.bulbScale,
              spacingScale: d.spacingScale,
              brightness: d.brightness,
              sag: d.sag,
              depthStart: d.depthStart,
              depthEnd: d.depthEnd,
              kind:
                st.tool === 'wrap'
                  ? ('wrap' as const)
                  : st.tool === 'swag'
                    ? ('swag' as const)
                    : st.tool === 'freehand'
                      ? ('freeform' as const)
                      : ('roofline' as const),
              wrapWidth: d.wrapWidth,
              wrapTurns: d.wrapTurns,
              wrapCap: d.wrapCap,
              capPalette: d.capPalette,
              capLength: d.capLength,
              capFronds: d.capFronds,
              capDroop: d.capDroop,
            }
          : null,
      )
    }
    const stats = lastStatsRef.current

    // Company logo, burned into the picture rather than added at export, so
    // what you see in the editor is exactly what the customer receives.
    const brand = st.doc.branding
    if (brand.logoDataUrl !== lastLogoRef.current) {
      lastLogoRef.current = brand.logoDataUrl
      refs.brandLayer.removeChildren().forEach((c) => c.destroy())
      brandSpriteRef.current = null
      if (brand.logoDataUrl) {
        const img = new Image()
        img.onload = () => {
          if (!refsRef.current) return
          const sp = new Sprite(Texture.from(img))
          refsRef.current.brandLayer.addChild(sp)
          brandSpriteRef.current = sp
          layoutBranding(sp, useEditor.getState().doc)
          sp.visible = useEditor.getState().doc.branding.show
        }
        img.src = brand.logoDataUrl
      }
    }
    if (brandSpriteRef.current) {
      layoutBranding(brandSpriteRef.current, st.doc)
      brandSpriteRef.current.visible = brand.show
    }

    const sel = st.doc.layers.find((l) => l.id === st.selectedId)
    drawOverlay(refs, {
      photoWidth: photo.width,
      photoHeight: photo.height,
      viewScale: viewRef.current.zoom,
      draft: st.draft,
      hover: st.hover,
      selected: sel && isStrand(sel) ? (sel as Strand) : null,
      selectedDecor:
        sel && isDecoration(sel) && !st.present
          ? {
              x: sel.x * photo.width,
              y: sel.y * photo.height,
              ...decorationSize(st.doc, sel),
            }
          : null,
      /*
       * ONLY THE DRAFT, NEVER THE SAVED MEASUREMENT.
       *
       * Falling back to the stored refA and refB meant every scaled job wore a
       * bright green line with two handles across whatever was measured -
       * usually straight over the garage door - in the editor, in Present, and
       * in the exported JPEG that gets sent to the customer. Clearing the
       * draft on apply was not enough, because this put it straight back.
       *
       * The measurement is a property of the document. It does not need to be
       * drawn on the picture to still be true.
       */
      calibA: st.calibDraft.a,
      calibB: st.calibDraft.b,
      showCrop: st.doc.scene.crop169,
      // Present mode hides the whole overlay, so the customer never sees a
      // dashed line or a circle - only the lights that were confirmed.
      proposals: st.doc.proposals,
      selectedProposalId: st.selectedProposalId,
    })
    // Present mode is what the customer sees. No handles, no guides.
    refs.overlay.visible = !st.present

    // Report the selection's position so the contextual badge can sit near it.
    if (onSelectionBox) {
      if (sel && isStrand(sel) && sel.points.length && !st.present) {
        const v = viewRef.current
        let minX = Infinity
        let maxX = -Infinity
        let minY = Infinity
        let maxY = -Infinity
        for (const p of sel.points) {
          const x = p.x * photo.width * v.zoom + v.x
          const y = p.y * photo.height * v.zoom + v.y
          if (x < minX) minX = x
          if (x > maxX) maxX = x
          if (y < minY) minY = y
          if (y > maxY) maxY = y
        }
        onSelectionBox({ midX: (minX + maxX) / 2, midY: (minY + maxY) / 2, top: minY, bottom: maxY })
      } else {
        onSelectionBox(null)
      }
    }

    /*
     * NIGHT IS A UNIFORM WRITE.
     *
     * It used to be a cross-fade between the photograph and a CPU-graded copy
     * of it, rebuilt whenever the settings changed - which is why the grade
     * was quantised to twenty steps and why dragging the dial stuttered: each
     * distinct step was a full pass over the image before a frame could be
     * drawn.
     *
     * Now the grade is a GPU pass over the photo layer with every parameter as
     * a uniform, so a drag is a few float writes and there is nothing to
     * rebuild, nothing to quantise, and no reason for the dial to be anything
     * but continuous.
     */
    const sc = st.doc.scene
    const ng = night()
    // The production control: one number, day to night. Presets are internal,
    // and while a test or the debug panel is driving them this must not fight
    // it - the redraw runs every frame and would win.
    if (!ng.isInternal()) ng.setIntensity(sc.night)
    ng.setMono(sc.nightMono === true)

    onStats(stats)
  }, [onStats, onSelectionBox, night])

  /* ---------------- setup ---------------- */

  /**
   * RENDERER READINESS, AS A DEPENDENCY.
   *
   * The Pixi app is initialized asynchronously, and the effects that draw into
   * it start with `if (!refsRef.current) return`. A ref is invisible to React,
   * so an effect that bails on a null ref is never retried when the ref fills
   * in - it waits for its own dependencies to change instead.
   *
   * That is fine when the thing it waits for arrives after a person has done
   * something, and wrong on a reload: the saved job comes back out of
   * IndexedDB in a few milliseconds, faster than app.init() resolves, so the
   * photo effect ran once against a null ref and never again. The job reopened
   * with its panel, its title and its runs, on a black stage, at 100% zoom
   * because fit() lives at the end of that same effect.
   *
   * One counter, bumped once when the renderer exists, turns that into a
   * dependency React can see.
   */
  const [rendererReady, setRendererReady] = useState(0)

  useEffect(() => {
    let canceled = false
    const host = hostRef.current
    if (!host) return

    const app = new Application()
    app
      .init({
        /*
         * THE GROUND AROUND THE PHOTOGRAPH, AND THE RENDERER OWNS IT.
         *
         * The stage element's own background is behind the canvas and is
         * never seen; what a person actually looks at either side of a
         * letterboxed photo is whatever Pixi clears to. This was a blue black
         * left over from when the whole app was black, so the one part of the
         * editor the design package is specific about - a dark green charcoal
         * viewing surface - was the one part still wearing the old color.
         *
         * It is the clear color and nothing else: no pixel of the photograph
         * and no part of the night grade goes through it.
         */
        background: '#1c1e21',
        antialias: true,
        resolution: Math.min(window.devicePixelRatio || 1, 2),
        autoDensity: true,
        resizeTo: host,
        preference: 'webgl',
      })
      .then(() => {
        if (canceled) {
          app.destroy(true)
          return
        }
        appRef.current = app
        host.appendChild(app.canvas)
        app.canvas.style.touchAction = 'none'
        app.canvas.style.display = 'block'

        const refs = createSceneRefs()
        /*
         * THE LAYER GUARANTEE.
         *
         * The grade attaches to the photo layer and to nothing else. The
         * bulbs, their glow, the wires, the measurement line, the drawing
         * handles, the selection outlines, the logo and the overlay are all
         * SIBLINGS of this layer, not children of it, so the grade cannot
         * reach them - not by tuning, not by accident, and not by a later edit
         * that forgets the rule. That is why the filter goes here and not on
         * the world container.
         */
        night().attachTo(refs.photoLayer)

        /*
         * A test seam, opt in by query string.
         *
         * The batch harness exists to find failure cases across thirty houses,
         * and it is worth much less if it drives a reimplementation of the
         * grade rather than the shader that actually ships. This lets it set a
         * preset and read back the scene statistics directly.
         *
         * Only when ?lptest=1 is present, so nothing is exposed to a
         * contractor's browser in normal use.
         */
        if (typeof window !== 'undefined' && window.location.search.includes('lptest')) {
          ;(window as unknown as Record<string, unknown>).__lpNight = {
            renderer: () => nightRef.current,
            setPreset: (id: 'dusk' | 'night' | 'darknight') =>
              useEditor.getState().setScene({ nightPreset: id }),
            setStrength: (v: number) => useEditor.getState().setScene({ night: v }),
            /** Production: through the document, exactly as the app does it. */
            forceProduction: (v: number) => {
              nightRef.current?.clearTestOverride()
              useEditor.getState().setScene({ night: v })
            },
            /** Internal presets, held against the redraw. */
            forceInternal: (id: 'dusk' | 'night' | 'darknight', strength: number) =>
              nightRef.current?.setTestPreset(id, strength),
            uniformStrength: () => nightRef.current?.getUniformStrength() ?? -1,
            /** Sweep the bulb look against a night background. Test only. */
            setBulbLook: (patch: Record<string, number>) => {
              setBulbLook(patch)
              /*
               * Clearing the document marker is what makes this actually
               * redraw. redraw() rebuilds the lights only when the DOCUMENT
               * changed, and the bulb look is a module constant, not part of
               * the document - so without this the call returns having done
               * nothing. It cost a whole comparison sheet of four
               * byte-identical images that I read differences into.
               */
              lastDocRef.current = null
              redrawRef.current?.()
            },
            /** Read the look back, so a test can prove the patch landed. */
            bulbLook: () => ({ ...BULB }),
            effectiveStrength: () => nightRef.current?.getEffectiveStrength() ?? -1,
            stats: () => nightRef.current?.getStats() ?? null,
            compensation: () => nightRef.current?.getCompensation() ?? 0,
            backoff: () => nightRef.current?.getBackoff() ?? 0,
            darkLift: () => nightRef.current?.getDarkLift() ?? 0,
            resolved: () => nightRef.current?.getResolved() ?? null,
            setDebugMagenta: (on: boolean) => nightRef.current?.setDebugMagenta(on),
            isActive: () => nightRef.current?.isActive() ?? false,
            /*
             * Not "a filter exists" - THIS filter, on THAT layer. The layer
             * separation is the guarantee that grading can never touch the
             * bulbs, and a guarantee nothing checks is a hope.
             */
            filterOnPhotoLayer: () => {
              const refs = refsRef.current
              const f = nightRef.current?.filter
              if (!refs || !f) return false
              const onPhoto = (refs.photoLayer.filters as unknown[] | null) ?? []
              const leaked = [
                refs.glowLayer, refs.coreLayer, refs.spillLayer,
                refs.wireLayer, refs.overlay, refs.brandLayer, refs.world,
              ].some((l) => {
                const f = (l as unknown as { filters?: readonly unknown[] | null }).filters
                return !!f && f.length > 0
              })
              return onPhoto.includes(f) && !leaked
            },
            /*
             * WHERE THE PHOTOGRAPH ACTUALLY IS ON SCREEN.
             *
             * Taken from the view transform, not guessed from pixels. The
             * stage fits the photo inside the canvas and letterboxes the rest
             * with the app background; a harness that measures the whole
             * canvas is measuring up to 43% padding, which drags every median,
             * every black percentage and every readability threshold darker
             * than the truth. It did exactly that, and the numbers looked like
             * a grading problem.
             */
            photoRect: () => {
              const app = appRef.current
              const ph = useEditor.getState().doc.photo
              if (!app || !ph) return null
              const v = viewRef.current
              const r = app.canvas.getBoundingClientRect()
              return {
                x: r.left + v.x,
                y: r.top + v.y,
                width: ph.width * v.zoom,
                height: ph.height * v.zoom,
              }
            },
          }
        }
        refsRef.current = refs
        app.stage.addChild(refs.world)

        onReady({ exportBlob, fit, zoomBy, night: () => nightRef.current })
        redraw()
        // Wake anything that gave up while this was still initializing.
        if (!canceled) setRendererReady((n) => n + 1)
      })

    return () => {
      canceled = true
      const a = appRef.current
      if (a) {
        a.destroy(true, { children: true, texture: false })
        appRef.current = null
        refsRef.current = null
        photoSpriteRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* ---------------- photo ---------------- */

  const photo = useEditor((s) => s.doc.photo)
  useEffect(() => {
    const refs = refsRef.current
    if (!refs) return
    /*
     * NO PHOTO MEANS AN EMPTY STAGE.
     *
     * This used to return early and leave everything it had drawn sitting
     * there, so starting a new job put the drop-the-photo panel on top of the
     * last customer's house with their lights still burning on it. It reads as
     * the app having failed to do the one thing you just asked it to do, and
     * on a tablet in front of the next customer it is showing them somebody
     * else's job.
     */
    if (!photo) {
      refs.photoLayer.removeChildren().forEach((c) => c.destroy())
      photoSpriteRef.current = null
      nightRef.current?.setSource(null)
      sourceImgRef.current = null
      renderLights(refs, useEditor.getState().doc, null)
      return
    }
    /*
     * Reject a photo that is obviously not one, before asking the browser.
     *
     * Relying on the image's own onerror is not enough: for a data URL like
     * "data:," browsers do not agree on whether that is an error or a zero
     * sized success, and on the one that matters here it was neither, so the
     * job sat on a black canvas with its lights hanging in space. This is the
     * shape the failure actually takes and it is cheap to spot.
     */
    if (!photo.dataUrl.startsWith('data:image/') || photo.dataUrl.length < 1000) {
      // eslint-disable-next-line no-console
      console.warn('[lightsos] this job has a photo that is not an image; clearing it')
      useEditor.getState().dropPhoto()
      return
    }

    let dead = false

    const img = new Image()
    /*
     * A PHOTO THAT WILL NOT DECODE MUST NOT BE A BLACK SCREEN.
     *
     * There was no onerror here at all. If the image failed - a truncated data
     * URL, an encode that silently produced "data:,", storage that came back
     * damaged - onload simply never fired: nothing drawn, no fit, no message,
     * and the panel carrying on as though there were a photo because as far as
     * the document is concerned there is one. The user is left looking at a
     * black rectangle with every control enabled and no way forward, which is
     * precisely how it was reported.
     *
     * Clearing the photo is the honest response. It puts the drop-the-photo
     * screen back, which is somewhere you can act from.
     */
    img.onerror = () => {
      if (dead) return
      // eslint-disable-next-line no-console
      console.warn('[lightsos] the photo on this job could not be decoded; clearing it')
      useEditor.getState().dropPhoto()
    }
    img.onload = () => {
      if (dead || !refsRef.current) return
      // A zero sized decode is a failure that arrived through the success path.
      if (!img.naturalWidth || !img.naturalHeight) {
        useEditor.getState().dropPhoto()
        return
      }
      const sprite = new Sprite(Texture.from(img))
      sprite.width = photo.width
      sprite.height = photo.height
      refs.photoLayer.removeChildren().forEach((c) => c.destroy())
      refs.photoLayer.addChild(sprite)
      photoSpriteRef.current = sprite

      sourceImgRef.current = img
      /*
       * Set the night look to suit this photo the moment it loads, without
       * turning night on. The scrubber then does the right thing on the first
       * drag instead of producing a black rectangle that has to be dialed back
       * by hand. Enhance does the same thing and turns night on with it.
       */
      try {
        const sug = suggestNight(img)
        useEditor.getState().setScene({
          nightDarkness: sug.darkness,
          nightSky: sug.sky,
          nightContrast: sug.contrast,
          nightColor: sug.color,
        })
      } catch {
        /* fall back to the stored defaults */
      }
      night().setSource(img)

      // Edge map still powers snap-to-edge while drawing by hand.
      try {
        useEditor.getState().setEdgeMap(buildEdgeMap(img))
      } catch {
        useEditor.getState().setEdgeMap(null)
      }

      // The roof is found once, from the sky boundary rather than from
      // brightness, so a tile roof against a bright sky is still found.
      try {
        roofRef.current = detectRoof(img)
      } catch {
        roofRef.current = null
      }
      fit()
    }
    img.src = photo.dataUrl
    return () => {
      dead = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo?.dataUrl, rendererReady])

  /*
   * Decoration artwork decodes asynchronously, and the first render after a
   * wreath is placed almost always lands before the image is ready. Without
   * this the renderer falls through to the drawn fallback and nothing ever
   * asks for the second draw, so the photograph never appears at all.
   */
  useEffect(() => {
    setAssetReadyCallback(() => {
      // redraw() rebuilds the lights only when the document changed, and an
      // image finishing its decode does not change the document. Clearing the
      // cache marker is what makes the second pass actually redraw.
      lastDocRef.current = null
      redraw()
    })
    return () => setAssetReadyCallback(null)
  }, [redraw])

  /* ---------------- redraw on any document change ---------------- */

  useEffect(() => {
    redrawRef.current = redraw
    const unsub = useEditor.subscribe(() => redraw())
    return unsub
  }, [redraw])

  // A window listener misses the panel opening and closing, which changes the
  // stage width without changing the window. Watch the element itself.
  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    let t: ReturnType<typeof setTimeout>
    const ro = new ResizeObserver(() => {
      clearTimeout(t)
      t = setTimeout(() => fit(), 60)
    })
    ro.observe(host)
    return () => {
      clearTimeout(t)
      ro.disconnect()
    }
  }, [fit])

  /* ---------------- keyboard ---------------- */

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const st = useEditor.getState()
      const typing =
        document.activeElement instanceof HTMLInputElement ||
        document.activeElement instanceof HTMLTextAreaElement
      if (typing) return

      /*
       * NUDGE WITH THE ARROWS.
       *
       * A run that landed a course too high wants moving a few pixels, and
       * doing that by dragging means re-aiming a mouse at a thin line and
       * hoping you do not overshoot. Arrows move it one screen pixel, shift
       * moves it ten. Works on a decoration too, so a wreath can be centerd on
       * a gable exactly rather than nearly.
       */
      if (!st.draft && st.selectedId && e.key.startsWith('Arrow')) {
        const ph = st.doc.photo
        const sel = st.doc.layers.find((l) => l.id === st.selectedId)
        if (ph && sel) {
          e.preventDefault()
          const step = (e.shiftKey ? 10 : 1) / viewRef.current.zoom
          const dx = (e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0) / ph.width
          const dy = (e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0) / ph.height
          if (dx || dy) {
            if (isStrand(sel)) {
              st.updateStrand(sel.id, {
                points: sel.points.map((p) => ({
                  x: Math.min(1, Math.max(0, p.x + dx)),
                  y: Math.min(1, Math.max(0, p.y + dy)),
                })),
              })
            } else if (isDecoration(sel)) {
              st.updateDecoration(sel.id, {
                x: Math.min(1, Math.max(0, sel.x + dx)),
                y: Math.min(1, Math.max(0, sel.y + dy)),
              })
            }
            redraw()
          }
          return
        }
      }

      if (e.key === 'Enter' && st.draft) {
        e.preventDefault()
        st.commitDraft()
      } else if (e.key === 'Escape') {
        /*
         * ESCAPE PUTS THE TOOL DOWN, not only the half-drawn line.
         *
         * It threw away the draft and left the tool armed, so the next press
         * anywhere started another one. Reported as: sometimes I click on the
         * picture and it automatically starts dragging a roof line. One press
         * of Escape now gets you all the way back to nothing happening.
         */
        st.cancelDraft()
        if (!st.draft && st.tool !== 'select') st.setTool('select')
      } else if (e.key === 'Backspace' && st.draft) {
        e.preventDefault()
        st.popDraftPoint()
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && st.selectedId) {
        st.deleteLayer(st.selectedId)
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault()
        if (e.shiftKey) st.redo()
        else st.undo()
      } else if (e.key === ' ') {
        // Not preventDefault on the way in: the page does not scroll, and a
        // person typing in a field elsewhere still gets their space.
        spaceRef.current = true
      } else if (e.key === '1') st.setTool('select')
      else if (e.key === '2') st.setTool('roofline')
      else if (e.key === '3') st.setTool('freehand')
      else if (e.key === 'f') fit()
    }
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === ' ') spaceRef.current = false
    }
    // Alt-tabbing away with space held would otherwise leave it stuck down.
    const onBlur = () => {
      spaceRef.current = false
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
    }
  }, [fit])

  /* ---------------- pointer ---------------- */

  const onPointerDown = (e: React.PointerEvent) => {
    ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
    const st = useEditor.getState()
    const world = toDoc(e.clientX, e.clientY)
    downRef.current = { x: e.clientX, y: e.clientY, world, moved: false, t: Date.now() }

    /*
     * HOLD SPACE AND DRAG, WHATEVER TOOL IS ARMED.
     *
     * The second way out of the same trap, for a mouse. Middle button already
     * did this and almost nobody has a middle button they think of; space is
     * the gesture every drawing program has taught for thirty years, and it
     * needs no trip to a toolbar and no trip back.
     */
    if (st.tool === 'pan' || e.button === 1 || spaceRef.current) {
      const v = viewRef.current
      panRef.current = { x: e.clientX, y: e.clientY, vx: v.x, vy: v.y }
      // Closed while it moves, open again on release. See the cleanup below.
      if (hostRef.current) hostRef.current.style.cursor = 'grabbing'
      return
    }
    if (!world) return

    /*
     * GRAB THE RUN YOU JUST DREW, WHATEVER TOOL IS ARMED.
     *
     * A roofline lands close but a course too high, and the fix should be to
     * push it down - the way you would nudge anything in Canva. That only
     * worked with the select tool active, so in practice the drawing tool was
     * still armed and the drag started a NEW run across the roof instead.
     *
     * The selected run's own line is unambiguous: a press on it means move it.
     * Anywhere else still draws.
     */
    if (st.doc.photo && !st.draft) {
      const ph2 = st.doc.photo
      const wpd = toPx(world, ph2.width, ph2.height)
      const tol = 16 / viewRef.current.zoom

      /*
       * A CIRCLE ON THE SELECTED RUN IS GRABBED BEFORE ANYTHING ELSE.
       *
       * It has to beat every other target on the photo, or a suggested run
       * lying along a confirmed one is unadjustable: the confirmed run's
       * Strand would win the layer test underneath and the suggestion could
       * never be picked up. Selection is what earns that priority, and
       * selection is one tap away.
       */
      const ed = st.selectedProposalId
        ? st.doc.proposals.find((q) => q.id === st.selectedProposalId)
        : undefined
      const grabbed = selectedProposalGrab(ed, wpd, ph2.width, ph2.height, viewRef.current.zoom)
      if (ed && grabbed) {
        st.beginRunEdit()
        if (grabbed.kind === 'vertex') dragProposalRef.current = { id: ed.id, index: grabbed.index }
        else
          dragProposalRunRef.current = {
            id: ed.id,
            from: world,
            points: ed.points.map((q) => ({ ...q })),
          }
        return
      }

      /*
       * ANY run, not just the selected one.
       *
       * This used to test only the currently selected run, which meant that the
       * moment you drew a second one the first became unreachable: clicking on
       * it started a new run across it instead of picking it up. A finished run
       * should be grabbable forever, the way anything on a canvas is.
       *
       * Topmost wins, so a run drawn later sits over an earlier one, and the
       * press both selects it and begins the move - one gesture, not two.
       */
      /*
       * SELECT FIRST, THEN MOVE. One rule, and it settles three conflicts.
       *
       * Making every run grabbable on contact broke drawing: a press within
       * sixteen pixels of anything picked that thing up, so a new run could not
       * start on top of an old one - and worse, a gable's second run starts
       * exactly where the first ended, so sweeping from that corner grabbed the
       * first run's endpoint instead. Measured: three roofline sweeps produced
       * two runs.
       *
       * So a run has to be SELECTED before it can be grabbed, and a tap on an
       * unselected run selects it rather than drawing. That gives:
       *   tap an old run          -> it selects, handles appear
       *   drag its body           -> moves it
       *   drag an end             -> runs it longer, straight
       *   tap empty photo         -> deselects, drawing resumes
       *   draw from a shared corner -> nothing is selected there, so it draws
       *
       * Which is what every canvas does, and what was actually being asked for.
       */
      let hit: typeof st.doc.layers[number] | null = null
      let bestD = tol
      for (let i = st.doc.layers.length - 1; i >= 0; i--) {
        const l = st.doc.layers[i]
        if (!isStrand(l) || !l.visible) continue
        const d = distanceToPath(
          l.points.map((p) => toPx(p, ph2.width, ph2.height)),
          wpd,
        )
        // Each run gets a target the size of the thing you can see.
        if (d <= Math.max(bestD, hitTolerance(l, ph2.width, viewRef.current.zoom))) {
          bestD = d
          hit = l
          break
        }
      }

      /*
       * A CONFIRMED RUN IS DRAGGED AS ITS PROPOSAL.
       *
       * The drag paths below write points straight onto the layer for speed,
       * which for a confirmed roofline would move the priced Strand while its
       * confirmation still described the old line. Rerouting here means the
       * confirmation is withdrawn on the first frame of the drag, which is
       * what requirement 1 asks for and also what the person sees: the lights
       * turn back into a dashed proposal the moment they move it.
       */
      const ownedBy = hit && isStrand(hit) ? st.doc.proposals.find((q) => q.strandId === hit.id) : undefined
      if (hit && isStrand(hit) && ownedBy) {
        if (st.selectedId !== hit.id) {
          st.selectLayer(hit.id)
          st.selectProposal(ownedBy.id)
          return
        }
        const grabEnd2 = 20 / viewRef.current.zoom
        for (const idx of [0, hit.points.length - 1]) {
          const e3 = toPx(hit.points[idx], ph2.width, ph2.height)
          if (Math.hypot(e3.x - wpd.x, e3.y - wpd.y) <= grabEnd2) {
            st.beginRunEdit()
            dragProposalRef.current = { id: ownedBy.id, index: idx }
            return
          }
        }
        st.beginRunEdit()
        dragProposalRunRef.current = {
          id: ownedBy.id,
          from: world,
          points: ownedBy.points.map((q) => ({ ...q })),
        }
        return
      }

      if (hit && isStrand(hit)) {
        if (st.selectedId !== hit.id) {
          /*
           * Not yours yet: this press claims it, and draws nothing.
           *
           * But it arms a sweep as well, because the next run on a gable
           * starts exactly where the last one ended - on top of it - and
           * without this the press would only ever select the run you just
           * drew. The sweep needs travel to activate, so a tap here is still
           * only a selection.
           */
          st.selectLayer(hit.id)
          if (!st.draft) sweepRef.current = { from: world, active: false }
          return
        }

        // Already selected, so the intent is to reshape it.
        const grabEnd = 20 / viewRef.current.zoom
        for (const idx of [0, hit.points.length - 1]) {
          const e2 = toPx(hit.points[idx], ph2.width, ph2.height)
          if (Math.hypot(e2.x - wpd.x, e2.y - wpd.y) <= grabEnd) {
            dragPointRef.current = { strandId: hit.id, index: idx }
            return
          }
        }
        dragRunRef.current = { id: hit.id, from: world, points: hit.points, pending: true }
        return
      }

      /*
       * A TAP ON A SUGGESTED RUN CLAIMS IT.
       *
       * Last, because a run already in the design owns a Strand and that
       * Strand is what the layer test above finds; a suggestion has no layer
       * at all, so without this it could only ever be reached from the list in
       * the side panel.
       *
       * It selects and returns. It does NOT also begin the move, and it does
       * not arm a sweep: a suggested run has circles drawn on it, so a press
       * that both claimed and dragged would make the run under the finger
       * jump on the tap that was only meant to reach it.
       */
      const picked = proposalPick(st.doc.proposals, wpd, ph2.width, ph2.height, viewRef.current.zoom)
      if (picked && picked !== st.selectedProposalId) {
        st.selectProposal(picked)
        return
      }
    }

    if (st.tool === 'freehand') {
      st.startDraft(world)
      freehandRef.current = world
      return
    }

    /*
     * PRESS AND DRAG STILL DRAWS, EVEN WITH NOTHING ARMED.
     *
     * Finishing a run drops the tool to select so that a stray click cannot
     * begin the next one. That would cost a tap on every single run if the
     * sweep gesture stopped working too, so a press on empty photo in select
     * mode arms the sweep for whatever the current category draws. It only
     * becomes a run once the pointer has actually traveled, so a plain tap
     * here is still just a tap.
     */

    /*
     * DRAW IT WITH YOUR FINGER.
     *
     * Tapping corner by corner is precise, and on a phone in a driveway it is
     * also fiddly. Sweeping an upside-down U over the garage door describes the
     * same shape in one motion and is what a hand wants to do, so a press and
     * drag on a run tool captures the stroke, and straightenRun turns the mess
     * into the three straight pieces and two corners that were meant.
     *
     * It costs nothing: a press that does not move is still a tap, because
     * nothing here commits until the pointer has actually traveled.
     */
    if (
      (st.tool === 'swag' ||
        st.tool === 'roofline' ||
        st.tool === 'auto' ||
        st.tool === 'wrap' ||
        st.tool === 'cluster' ||
        st.tool === 'wreath') &&
      !st.draft
    ) {
      sweepRef.current = { from: world, active: false }
      return
    }

    if (st.tool === 'select') {
      /*
       * A HANDLE BEATS EVERYTHING ELSE UNDER THE CURSOR.
       *
       * The corners sit ON the decoration, so if the "am I inside this thing"
       * test ran first every attempt to resize would move it instead. Handles
       * are tested first and with a generous radius, because they are what the
       * user is aiming at when they aim at one.
       */
      const phh = st.doc.photo
      const selNow = st.doc.layers.find((l) => l.id === st.selectedId)
      if (phh && selNow && isDecoration(selNow)) {
        const { w, h } = decorationSize(st.doc, selNow)
        const cx = selNow.x * phh.width
        const cy = selNow.y * phh.height
        const wp = toPx(world, phh.width, phh.height)
        const grab = 22 / viewRef.current.zoom
        const x0 = cx - w / 2
        const y0 = cy - h / 2
        const spots: { x: number; y: number; corner: boolean }[] = [
          { x: x0, y: y0, corner: true },
          { x: x0 + w, y: y0, corner: true },
          { x: x0, y: y0 + h, corner: true },
          { x: x0 + w, y: y0 + h, corner: true },
        ]
        if (w / h > 1.6 || h / w > 1.6) {
          spots.push({ x: x0, y: cy, corner: false }, { x: x0 + w, y: cy, corner: false })
        }
        for (const sp of spots) {
          if (Math.hypot(sp.x - wp.x, sp.y - wp.y) <= grab) {
            resizeRef.current = {
              id: selNow.id,
              corner: sp.corner,
              // Scale away from the opposite corner, the way every design tool
              // does it: the corner you are not holding stays put.
              ax: cx + (cx - sp.x),
              ay: cy + (cy - sp.y),
              scale0: selNow.scale,
              scaleY0: selNow.scaleY,
              w0: w,
              h0: h,
            }
            return
          }
        }
      }

      // Grab a decoration anywhere inside it, so moving one is just dragging it.
      const ph = st.doc.photo
      if (ph) {
        const wp = toPx(world, ph.width, ph.height)
        for (let i = st.doc.layers.length - 1; i >= 0; i--) {
          const l = st.doc.layers[i]
          if (!isDecoration(l) || !l.visible) continue
          const { w, h } = decorationSize(st.doc, l)
          const cx = l.x * ph.width
          const cy = l.y * ph.height
          if (Math.abs(wp.x - cx) <= w / 2 && Math.abs(wp.y - cy) <= h / 2) {
            dragDecorRef.current = { id: l.id, dx: world.x - l.x, dy: world.y - l.y }
            return
          }
        }
      }

      // Grab an anchor of the selected strand if the tap landed on one
      const photoNow = st.doc.photo
      const sel = st.doc.layers.find((l) => l.id === st.selectedId)
      if (photoNow && sel && isStrand(sel)) {
        const wp = toPx(world, photoNow.width, photoNow.height)
        const grabPx = 18 / viewRef.current.zoom
        for (let i = 0; i < sel.points.length; i++) {
          const p = toPx(sel.points[i], photoNow.width, photoNow.height)
          if (Math.hypot(p.x - wp.x, p.y - wp.y) <= grabPx) {
            dragPointRef.current = { strandId: sel.id, index: i }
            return
          }
        }

        /*
         * Grab the RUN, not just its ends.
         *
         * Corner handles reshape it; the body moves it. A garland drawn one
         * course too low on the garage header should be nudged up as a piece,
         * and doing that by dragging each corner in turn - and getting the
         * shape slightly wrong every time - is not a thing anyone should have
         * to do. Same gesture a decoration already answers to.
         */
        if (
          distanceToPath(
            sel.points.map((p) => toPx(p, photoNow.width, photoNow.height)),
            wp,
          ) <=
          14 / viewRef.current.zoom
        ) {
          // Select tool: the intent is unambiguous, so move immediately.
          dragRunRef.current = { id: sel.id, from: world, points: sel.points, pending: false }
          return
        }
      }

      /*
       * PRESS AND DRAG STILL DRAWS, WITH NOTHING ARMED.
       *
       * Finishing a run drops the tool to select, so a stray click can no
       * longer begin the next one. If the sweep gesture stopped working too
       * that would cost a tap on every run, so a press that reaches here - the
       * select branch has already claimed anything it wanted, so this is empty
       * photo - arms a sweep for whatever this category draws.
       *
       * It only becomes a run once the pointer has traveled past the slop, so
       * a plain tap on empty photo is still just a tap.
       */
      if (
        !st.draft &&
        !dragRunRef.current &&
        !dragPointRef.current &&
        !dragProposalRef.current &&
        !dragProposalRunRef.current &&
        !dragDecorRef.current
      ) {
        sweepRef.current = { from: world, active: false }
      }
    }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    const st = useEditor.getState()
    const down = downRef.current

    /*
     * The cursor should say what this click will do.
     *
     * A crosshair everywhere is right for drawing and wrong for everything
     * else: hovering a resize handle with a crosshair reads as "you are about
     * to draw something here", which is the opposite of the truth. A pointing
     * finger over the handles and a grab hand over a decoration are what every
     * design tool uses and what the hand already expects.
     */
    if (!down) {
      const host = hostRef.current
      const w0 = toDoc(e.clientX, e.clientY)
      const phc = st.doc.photo
      if (host && phc && w0) {
        /*
         * A HAND ONLY WHERE DRAGGING REALLY PANS.
         *
         * Panning is the pan tool, the middle button or a held space bar, and
         * nothing else. An open hand over a photograph that will not move on a
         * drag is the cursor lying about what the next gesture does, so it is
         * shown for exactly the states that pan and the crosshair is kept off
         * them - the pan tool used to hand you a crosshair, which promised a
         * line where a drag moves the picture.
         */
        const panning = st.tool === 'pan' || spaceRef.current
        let cur = panning ? 'grab' : st.tool === 'select' ? 'default' : 'crosshair'

        /*
         * A hand over anything you can pick up, whatever tool is armed.
         *
         * A crosshair sitting on a finished run says "click here to draw", and
         * the truth is the opposite: click here and you will move this. The
         * cursor is the only thing telling you a run is grabbable at all.
         */
        {
          const wpH = toPx(w0, phc.width, phc.height)
          const tolH = 16 / viewRef.current.zoom
          const endH = 20 / viewRef.current.zoom

          /*
           * A SUGGESTED RUN MAKES THE SAME PROMISE AS ONE IN THE DESIGN.
           *
           * The cursor walked the priced layers only, so a suggestion - the
           * thing most likely to need nudging, because a model placed it -
           * sat under a crosshair saying "draw here" while the truth was
           * "drag me". The same three cases as below, from the same rule the
           * pointer handler uses, so the cursor cannot promise a gesture the
           * press will refuse.
           */
          const selP = st.selectedProposalId
            ? st.doc.proposals.find((q) => q.id === st.selectedProposalId)
            : undefined
          const grabP = selectedProposalGrab(selP, wpH, phc.width, phc.height, viewRef.current.zoom)
          if (selP && grabP) {
            if (grabP.kind === 'vertex') {
              const a = selP.points[0]
              const b = selP.points[selP.points.length - 1]
              cur = Math.abs(b.x - a.x) >= Math.abs(b.y - a.y) ? 'ew-resize' : 'ns-resize'
            } else {
              cur = 'grab'
            }
            if (host.style.cursor !== cur) host.style.cursor = cur
            return
          }

          outer: for (let i = st.doc.layers.length - 1; i >= 0; i--) {
            const l = st.doc.layers[i]
            if (!isStrand(l) || !l.visible) continue
            // An end says "you can make this longer"; the body says "you can
            // pick this up". Two different promises, two different cursors.
            for (const idx of [0, l.points.length - 1]) {
              const e = toPx(l.points[idx], phc.width, phc.height)
              if (Math.hypot(e.x - wpH.x, e.y - wpH.y) <= endH) {
                const a = l.points[0]
                const b = l.points[l.points.length - 1]
                const horizontal = Math.abs(b.x - a.x) >= Math.abs(b.y - a.y)
                cur = horizontal ? 'ew-resize' : 'ns-resize'
                break outer
              }
            }
            if (
              distanceToPath(
                l.points.map((p) => toPx(p, phc.width, phc.height)),
                wpH,
              ) <= Math.max(tolH, hitTolerance(l, phc.width, viewRef.current.zoom))
            ) {
              cur = 'grab'
              break outer
            }
          }

          /*
           * Nothing in the design under the pointer, but a run still waiting
           * to be included might be. A press there selects rather than moves,
           * so it gets the pointing finger and not the hand: one gesture, one
           * honest cursor.
           */
          if (cur === (panning ? 'grab' : st.tool === 'select' ? 'default' : 'crosshair')) {
            if (proposalPick(st.doc.proposals, wpH, phc.width, phc.height, viewRef.current.zoom)) {
              cur = 'pointer'
            }
          }
        }

        if (st.tool === 'select') {
          const selC = st.doc.layers.find((l) => l.id === st.selectedId)
          const wp0 = toPx(w0, phc.width, phc.height)
          if (selC && isDecoration(selC)) {
            const { w, h } = decorationSize(st.doc, selC)
            const cx0 = selC.x * phc.width
            const cy0 = selC.y * phc.height
            const gx = 22 / viewRef.current.zoom
            const onHandle = [
              [cx0 - w / 2, cy0 - h / 2],
              [cx0 + w / 2, cy0 - h / 2],
              [cx0 - w / 2, cy0 + h / 2],
              [cx0 + w / 2, cy0 + h / 2],
              [cx0 - w / 2, cy0],
              [cx0 + w / 2, cy0],
            ].some(([hx, hy]) => Math.hypot(hx - wp0.x, hy - wp0.y) <= gx)
            if (onHandle) cur = 'pointer'
            else if (Math.abs(wp0.x - cx0) <= w / 2 && Math.abs(wp0.y - cy0) <= h / 2) cur = 'grab'
          }
        }
        if (host.style.cursor !== cur) host.style.cursor = cur
      }
    }

    if (panRef.current) {
      const p = panRef.current
      viewRef.current.x = p.vx + (e.clientX - p.x)
      viewRef.current.y = p.vy + (e.clientY - p.y)
      applyView()
      return
    }

    if (down && !down.moved) {
      if (Math.hypot(e.clientX - down.x, e.clientY - down.y) > DRAG_SLOP) down.moved = true
    }

    const world = toDoc(e.clientX, e.clientY)
    if (!world) return

    const dd = dragDecorRef.current
    if (dd) {
      useEditor.setState((s2) => ({
        doc: {
          ...s2.doc,
          layers: s2.doc.layers.map((l) =>
            l.id === dd.id && l.type === 'decoration'
              ? { ...l, x: world.x - dd.dx, y: world.y - dd.dy }
              : l,
          ),
        },
      }))
      return
    }

    // Dragging a circle on a run that a proposal owns.
    const pd = dragProposalRef.current
    if (pd) {
      const pr = st.doc.proposals.find((q) => q.id === pd.id)
      if (pr) {
        const pts = pr.points.map((q) => ({ ...q }))
        pts[pd.index] = st.snapEnabled && st.edgeMap ? snapToEdge(st.edgeMap, world) : world
        st.editRunPointsLive(pr.id, pts)
      }
      return
    }

    const pr2 = dragProposalRunRef.current
    if (pr2) {
      const dx2 = world.x - pr2.from.x
      const dy2 = world.y - pr2.from.y
      st.editRunPointsLive(
        pr2.id,
        pr2.points.map((q) => ({
          x: Math.min(1, Math.max(0, q.x + dx2)),
          y: Math.min(1, Math.max(0, q.y + dy2)),
        })),
      )
      return
    }

    // Dragging an anchor of the selected strand
    const dp = dragPointRef.current
    if (dp) {
      const strand = st.doc.layers.find((l) => l.id === dp.strandId)
      if (strand && isStrand(strand)) {
        const pts = [...strand.points]
        let target = world

        /*
         * PULLING AN END EXTENDS THE RUN. It does not bend it.
         *
         * A roofline that stops a couple of feet short is the most common thing
         * to have to fix, and the fix is "make it longer", not "move this
         * corner somewhere new". Dragging the end used to do the second, so
         * lengthening a run by eye also put a kink in it and knocked it off the
         * fascia it had just been snapped to.
         *
         * So an END anchor slides along the run's own direction: swipe outward
         * and it grows, swipe back and it shortens, and it stays dead straight
         * either way. Pull properly sideways - more than a finger's width off
         * the line - and it goes back to free movement, because sometimes you
         * really do mean to move the corner.
         */
        const isEnd = dp.index === 0 || dp.index === pts.length - 1
        const ph2 = st.doc.photo
        if (isEnd && pts.length >= 2 && ph2) {
          const anchor = pts[dp.index === 0 ? 1 : pts.length - 2]
          const a = toPx(anchor, ph2.width, ph2.height)
          const w2 = toPx(world, ph2.width, ph2.height)
          const e = toPx(pts[dp.index], ph2.width, ph2.height)
          const dx = e.x - a.x
          const dy = e.y - a.y
          const len = Math.hypot(dx, dy)
          if (len > 1) {
            const ux = dx / len
            const uy = dy / len
            const along = (w2.x - a.x) * ux + (w2.y - a.y) * uy
            const perp = Math.abs((w2.x - a.x) * -uy + (w2.y - a.y) * ux)
            if (perp < ph2.width * 0.02) {
              // Keep at least a little run left; dragging past the neighbor
              // and inverting the segment is never what was meant.
              const keep = Math.max(len * 0.15, ph2.width * 0.01)
              const d = Math.max(keep, along)
              target = {
                x: (a.x + ux * d) / ph2.width,
                y: (a.y + uy * d) / ph2.height,
              }
            }
          }
        }

        pts[dp.index] = st.snapEnabled && st.edgeMap ? snapToEdge(st.edgeMap, target) : target
        // Direct set, no undo entry per mouse move
        useEditor.setState((s) => ({
          doc: {
            ...s.doc,
            layers: s.doc.layers.map((l) =>
              l.id === dp.strandId && isStrand(l) ? { ...l, points: pts } : l,
            ),
          },
        }))
      }
      return
    }

    const rz = resizeRef.current
    if (rz && down) {
      down.moved = true
      const ph3 = st.doc.photo
      if (ph3) {
        const wp = toPx(world, ph3.width, ph3.height)
        if (rz.corner) {
          // Diagonal drag: how much bigger the box is now than it was, judged
          // on whichever axis moved more, so the shape never flips or inverts.
          const fx = Math.abs(wp.x - rz.ax) / Math.max(1, rz.w0)
          const fy = Math.abs(wp.y - rz.ay) / Math.max(1, rz.h0)
          const f = Math.max(0.08, Math.max(fx, fy))
          const next = Math.max(0.15, Math.min(8, rz.scale0 * f))
          st.updateDecoration(rz.id, {
            scale: next,
            // Uniform: dragging a corner keeps the proportions, so a wreath
            // stays round. Stretching is what the side handles are for.
            scaleY: rz.scaleY0 === undefined ? undefined : next,
          })
        } else {
          // Side handle: longer, not fatter.
          const fx = Math.abs(wp.x - rz.ax) / Math.max(1, rz.w0)
          const next = Math.max(0.15, Math.min(8, rz.scale0 * Math.max(0.08, fx)))
          st.updateDecoration(rz.id, { scale: next, scaleY: rz.scaleY0 ?? rz.scale0 })
        }
      }
      return
    }

    const dr = dragRunRef.current
    if (dr && down) {
      // Not a move until the pointer has actually gone somewhere.
      if (dr.pending) {
        const traveled = Math.hypot(e.clientX - down.x, e.clientY - down.y)
        if (traveled < DRAG_SLOP) return
        dr.pending = false
      }
      down.moved = true
      const dx = world.x - dr.from.x
      const dy = world.y - dr.from.y
      const moved = dr.points.map((p) => ({
        x: Math.min(1, Math.max(0, p.x + dx)),
        y: Math.min(1, Math.max(0, p.y + dy)),
      }))
      useEditor.setState((s) => ({
        doc: {
          ...s.doc,
          layers: s.doc.layers.map((l) =>
            l.id === dr.id && isStrand(l) ? { ...l, points: moved } : l,
          ),
        },
      }))
      return
    }

    const sweep = sweepRef.current
    if (sweep && down) {
      const photoNow = st.doc.photo
      if (photoNow) {
        const a = toPx(sweep.from, photoNow.width, photoNow.height)
        const b = toPx(world, photoNow.width, photoNow.height)
        const traveled = Math.hypot(b.x - a.x, b.y - a.y)
        if (!sweep.active && traveled > photoNow.width * 0.012) {
          // Far enough to be a deliberate stroke rather than a shaky tap.
          sweep.active = true
          /*
           * Re-arm before the draft starts, not after.
           *
           * A sweep begun from select mode has to become a real run of the
           * current category, and commitDraft decides what kind of run it is
           * from the TOOL. Setting it once the stroke is committed is too
           * late: the run would already have been built as the wrong thing.
           */
          if (st.tool === 'select') useEditor.getState().setTool(st.drawToolForCategory())
          // A sweep that began on top of an existing run is a new run, not a
          // reshape of that one, so it must not leave it selected.
          useEditor.getState().selectLayer(null)
          useEditor.getState().startDraft(sweep.from)
          // A rough gesture meaning "that shape, there". commitDraft reads
          // this to decide whether it may straighten what came out of it; a
          // shape tapped out corner by corner is kept as tapped.
          useEditor.getState().markDraftSwiped()
          freehandRef.current = sweep.from
        }
        if (sweep.active) {
          const last = freehandRef.current
          if (last) {
            const l = toPx(last, photoNow.width, photoNow.height)
            if (Math.hypot(b.x - l.x, b.y - l.y) > photoNow.width * 0.006) {
              st.addDraftPoints([world])
              freehandRef.current = world
            }
          }
        }
      }
      return
    }

    if (st.tool === 'freehand' && st.draft && down) {
      const last = freehandRef.current
      const photoNow = st.doc.photo
      if (last && photoNow) {
        const a = toPx(last, photoNow.width, photoNow.height)
        const b = toPx(world, photoNow.width, photoNow.height)
        if (Math.hypot(b.x - a.x, b.y - a.y) > photoNow.width * 0.006) {
          st.addDraftPoints([world])
          freehandRef.current = world
        }
      }
      return
    }

    if (st.draft) st.setHover(world)
  }

  const onPointerUp = (e: React.PointerEvent) => {
    const st = useEditor.getState()
    const down = downRef.current
    downRef.current = null

    if (panRef.current) {
      panRef.current = null
      if (hostRef.current) hostRef.current.style.cursor = spaceRef.current ? 'grab' : ''
      return
    }
    if (dragProposalRef.current || dragProposalRunRef.current) {
      dragProposalRef.current = null
      dragProposalRunRef.current = null
      redraw()
      return
    }
    if (dragPointRef.current) {
      dragPointRef.current = null
      redraw()
      return
    }
    if (resizeRef.current) {
      resizeRef.current = null
      redraw()
      return
    }
    if (dragRunRef.current) {
      const wasMove = !dragRunRef.current.pending
      dragRunRef.current = null
      if (wasMove) {
        redraw()
        return
      }
      // A tap on a run: it is selected now, and the press carries on to the
      // drawing logic below so a run can still start here.
    }
    if (dragDecorRef.current) {
      const id = dragDecorRef.current.id
      dragDecorRef.current = null
      if (down?.moved) {
        redraw()
        return
      }
      // A tap rather than a drag: select it so the size controls appear.
      st.selectLayer(id)
      return
    }
    if (st.tool === 'freehand') {
      freehandRef.current = null
      if (st.draft && st.draft.length > 1) st.commitDraft()
      else st.cancelDraft()
      return
    }

    if (sweepRef.current) {
      const wasSweep = sweepRef.current.active
      sweepRef.current = null
      freehandRef.current = null
      if (wasSweep) {
        /*
         * Lifting off a sweep must not arm the next one.
         *
         * The release fires a click as well, and that click was landing in the
         * tap path and starting a fresh run the moment the finger came up - so
         * every sweep left a stray line hanging off the cursor.
         */
        quietUntilRef.current = Date.now() + 260

        /*
         * A ROOFLINE SWIPE MEANS "THAT EDGE", NOT "THIS LINE".
         *
         * Straightening polishes what the hand drew, which leaves the run short
         * at both ends and keeps every kink - a gable that is plainly one
         * straight line came back with six handles and a zigzag. The sky
         * boundary already knows where the real facets are and how far each one
         * runs, so if the stroke was aiming at one, take the WHOLE facet
         * instead. Miss everything and it falls through to straightening, which
         * never invents a roof that is not there.
         */
        if ((st.tool === 'auto' || st.tool === 'roofline') && roofRef.current?.length && st.draft) {
          const raw = facetForStroke(roofRef.current, st.draft)
          if (raw) {
            // Its angle and its length, at the height the stroke was drawn.
            const f = facetThroughStroke(raw, st.draft)
            st.cancelDraft()
            st.startDraft(f.a)
            st.addDraftPoints([f.b])
            useEditor.getState().commitDraft()
            freehandRef.current = null
            return
          }
        }

        /*
         * DRAW A CIRCLE, GET A WREATH THAT SIZE.
         *
         * Placing one and then hunting for a size slider is two steps for
         * something the hand already said: the loop you scribbled over the
         * gable IS the size you want. Center and mean radius of the stroke are
         * all it takes, and it works the same with a finger.
         */
        if (st.tool === 'wreath') {
          const pts = st.draft ?? []
          const ph = st.doc.photo
          if (pts.length > 3 && ph) {
            let cx = 0
            let cy = 0
            for (const p of pts) {
              cx += p.x
              cy += p.y
            }
            cx /= pts.length
            cy /= pts.length
            let r = 0
            for (const p of pts) {
              r += Math.hypot((p.x - cx) * ph.width, (p.y - cy) * ph.height)
            }
            r /= pts.length

            st.cancelDraft()
            st.addDecoration('wreath', { x: cx, y: cy })
            const made = useEditor.getState()
            const dec = made.doc.layers[made.doc.layers.length - 1]
            if (dec && isDecoration(dec)) {
              // decorationSize maps scale to a diameter of radius * 2.35, so
              // invert that to land on the circle the user actually drew.
              const unit = made.doc.calibration.pixelsPerFoot || ph.width * 0.045
              made.updateDecoration(dec.id, {
                scale: Math.max(0.2, Math.min(6, (r * 2) / (unit * 2.35))),
              })
            }
            redraw()
            return
          }
          st.cancelDraft()
          return
        }

        // commitDraft straightens it: corners found, each piece locked to the
        // architecture underneath. What was drawn with a thumb comes back
        // looking like it was measured.
        if (st.draft && st.draft.length > 1) st.commitDraft()
        else st.cancelDraft()
        return
      }
    }

    const world = toDoc(e.clientX, e.clientY)
    if (!world || !down) return
    if (down.moved) return // a drag, not a tap

    const photoNow = st.doc.photo
    if (!photoNow) return

    if (st.tool === 'calibrate') {
      st.setCalibPoint(world)
      return
    }

    if (st.tool === 'wreath') {
      st.addDecoration('wreath', world)
      return
    }

    if (st.tool === 'garlandArt') {
      st.addDecoration('garland', world)
      return
    }

    /*
     * Accessories place whichever product is currently picked, and take their
     * kind from that product - so a reindeer added to the library lands here
     * without this file changing.
     */
    if (st.tool === 'accessory') {
      const chosen = accessoryStyles().find((a) => a.id === st.decorStyle.bow) ?? accessoryStyles()[0]
      if (chosen) st.addDecoration(chosen.kind, world)
      return
    }

    if (st.tool === 'select') {
      let hit: string | null = null
      let best = Infinity

      /*
       * Decorations first, and topmost wins. They are solid objects sitting
       * over the photo, so a click inside one is unambiguous, whereas a strand
       * only claims a click within a few pixels of its line.
       */
      const wp0 = toPx(world, photoNow.width, photoNow.height)
      for (let i = st.doc.layers.length - 1; i >= 0; i--) {
        const l = st.doc.layers[i]
        if (!isDecoration(l) || !l.visible) continue
        const { w, h } = decorationSize(st.doc, l)
        const cx = l.x * photoNow.width
        const cy = l.y * photoNow.height
        if (Math.abs(wp0.x - cx) <= w / 2 && Math.abs(wp0.y - cy) <= h / 2) {
          st.selectLayer(l.id)
          return
        }
      }
      const wp = toPx(world, photoNow.width, photoNow.height)

      /*
       * PROPOSED RUNS ARE CLICKABLE TOO, AND THEY WIN A TIE.
       *
       * A dashed line is the app asking a question, so a tap near one should
       * open the question rather than select whatever is behind it. Confirmed
       * runs are reached through their Strand below, as any other run is.
       */
      let pHit: string | null = null
      let pBest = Infinity
      const pTol = (24 * (photoNow.width / 1400)) / Math.max(0.2, viewRef.current.zoom)
      for (const pr of st.doc.proposals) {
        if (pr.status !== 'proposed' || pr.points.length < 2) continue
        const d = distanceToPath(
          pr.points.map((q) => toPx(q, photoNow.width, photoNow.height)),
          wp,
        )
        if (d < pTol && d < pBest) {
          pBest = d
          pHit = pr.id
        }
      }
      if (pHit) {
        st.selectProposal(pHit)
        return
      }

      for (const l of st.doc.layers) {
        if (!isStrand(l) || !l.visible) continue
        const tol = hitTolerance(l, photoNow.width, viewRef.current.zoom)
        const d = distanceToPath(
          l.points.map((p) => toPx(p, photoNow.width, photoNow.height)),
          wp,
        )
        if (d < tol && d < best) {
          best = d
          hit = l.id
        }
      }
      // Selecting a confirmed run opens its card as well, so Edit and Remove
      // are in the same place whether the run is proposed or confirmed.
      const owner = hit ? st.doc.proposals.find((q) => q.strandId === hit) : undefined
      if (owner) st.selectProposal(owner.id)
      else st.selectProposal(null)
      st.selectLayer(hit)
      return
    }

    /*
     * NO MORE ONE-CLICK RUNS.
     *
     * The roofline tool used to place an entire detected facet on a single
     * click. It reads well in a demo and it is wrong here: one tap now makes a
     * whole run, so the two taps of an ordinary two-click roofline made TWO
     * runs stacked on the same roof - measured, one two-click roofline produced
     * two layers - and any stray tap on the photo left a run behind.
     *
     * A click starts a run and a second click finishes it, everywhere, with no
     * exceptions. The facet detector has not gone anywhere: it still snaps the
     * finished run onto the real roof edge and runs it out to the ends, which
     * is where the intelligence actually belonged.
     */

    /*
     * The click that ends a sweep is not a request to start another.
     *
     * Releasing a drag fires a click too, and it was landing here and starting
     * a fresh run the instant the finger came up, leaving a rubber band
     * trailing off the cursor after every swipe.
     *
     * Only the quiet window guards this. Judging a tap by how long it was held
     * was the first attempt and it was wrong: a decisive click is genuinely
     * short, so that rejected real taps and broke two-click drawing outright.
     */
    if (Date.now() < quietUntilRef.current) return

    // Drawing tools: place a point, snapped to the real roof edge
    const snapped = st.snapEnabled && st.edgeMap ? snapToEdge(st.edgeMap, world) : world

    /*
     * TWO CLICKS MAKE A RUN.
     *
     * Click where the line starts, click where it ends, and it is finished.
     * Nothing to press, nothing to type, no gesture to be told about. The next
     * click starts the next run.
     *
     * This costs a click at a corner, because a gable is two runs rather than
     * one polyline. That is the right trade: a run that never ends on its own
     * is the failure people actually hit, and every extra segment is separately
     * colorable and separately priced anyway.
     */
    /*
     * A CLICK STARTS THE RUN, AND THE ARMING IS WHAT SAYS YOU MEANT IT.
     *
     * The first point used to require a hundred and sixty millisecond hold.
     * That was the right fix for the app it was written in: the tool stayed
     * armed all day, so every idle tap on the photograph was the beginning of
     * something, reported three times as the line starting wherever I click.
     *
     * That app is gone. Nothing is armed when the door opens, committing a run
     * drops the tool back to select, and a category is only armed because
     * somebody just pressed it in the dock. The armed state is now the
     * statement of intent, and it lasts one run.
     *
     * So the hold had stopped buying anything and had started costing the
     * thing it was protecting: a decisive click is genuinely short - forty
     * milliseconds is normal - and those clicks did nothing at all. Reported
     * as: I am trying to do the roof, I click and it does not start.
     *
     * Time was the wrong axis. What separates a click from a sweep is
     * movement, and the sweep path is handled well before here. A stray tap
     * while a tool is armed now lays down one point, which is visible, and
     * Escape or the Done chip clears it - a far cheaper mistake than a click
     * that silently does nothing.
     */
    if (!st.draft) {
      st.startDraft(snapped)
      st.setHover(snapped)
      return
    }

    /*
     * MULTI-CORNER SHAPES, AND HOW YOU SAY YOU ARE DONE.
     *
     * A garage door is one piece of garland with corners in it, so those
     * categories keep taking points. The problem is ending, and the usual
     * answer - double click - is a desktop habit. On an iPad a double tap
     * already means zoom, so it fights the browser, and a contractor holding a
     * tablet in one hand in a driveway will never discover it anyway.
     *
     * Tapping the last corner a SECOND time ends the run. It is the same motion
     * with a finger, a stylus or a mouse, it collides with nothing, and it is
     * what a hand does naturally when it means "that one, and stop".
     *
     * A visible Done chip sits next to the open run as well, because a gesture
     * nobody can see is only half an answer. Both work; neither is required to
     * be learned.
     */
    const isMulti = !!CATEGORY_SETUP[st.category].multi
    if (isMulti && st.draft.length) {
      const last = st.draft[st.draft.length - 1]
      const photoPx = st.doc.photo
      const near =
        photoPx &&
        Math.hypot(
          (snapped.x - last.x) * photoPx.width,
          (snapped.y - last.y) * photoPx.height,
        ) *
          viewRef.current.zoom <
          18
      if (near) {
        if (st.draft.length > 1) useEditor.getState().commitDraft()
        else st.cancelDraft()
        return
      }
      st.addDraftPoints([snapped])
      st.setHover(snapped)
      return
    }

    st.addDraftPoints([snapped])

    /*
     * TWO TAPS ARE TWO DECISIONS, AND NOTHING HERE GETS TO OVERRULE THEM.
     *
     * Two things used to run at exactly this point and both replaced the
     * geometry the person had just pointed at.
     *
     *   traceRun     followed the roof edge between the taps and swapped the
     *                two points for the traced polyline - whatever length the
     *                tracer happened to find.
     *   facetForStroke  matched the taps to a detected roof facet and replaced
     *                them with that facet's two ends.
     *
     * Reported first as: I drew the endpoint where that arrow is and it
     * extended farther, I did not put it that far. Then, after the straightener
     * was taught to keep the length: the roofline is not always going the
     * length of the dots, it is shorter many times. The second report is the
     * proof that the fix was in the wrong place - the draft never arrived at
     * straightenRun as the two points that were clicked, so protecting it there
     * protected nothing.
     *
     * This code path is only ever reached by a TAP: the handler returns early
     * on down.moved, so a swipe never gets here. Which means these two were
     * only ever running on the one gesture they should never run on. A swipe
     * still gets all of it, in the sweep path, where a rough "that edge, there"
     * is genuinely what was meant.
     *
     * Snapping stays. It moves the line perpendicular onto the fascia and
     * cannot move either end along it.
     */
    useEditor.getState().commitDraft()
  }

  const onDoubleClick = () => {
    const st = useEditor.getState()
    if (st.draft) st.commitDraft()
  }

  /**
   * SCROLL MOVES THE PHOTO. PINCH ZOOMS IT.
   *
   * Two complaints, and they are the same complaint from both ends. First:
   * when I use the mouse it kind of zooms in and out and I can't read the
   * picture. Then, once zoomed: how do I move around, if I click anywhere it
   * starts putting lines.
   *
   * Scroll used to zoom, which left no way at all to move a magnified photo
   * except finding the Move button in the corner and switching tools - and a
   * tool you have to switch to and back out of is a tool nobody uses while
   * they are working.
   *
   * So this is the arrangement every map and every canvas tool has settled on:
   * two fingers move the picture, pinch changes the size of it. A trackpad
   * pinch arrives here as a wheel event with ctrlKey set, which is what makes
   * both fit through one handler. Cmd is accepted too, for a mouse wheel,
   * which cannot pinch.
   */
  const onWheel = (e: React.WheelEvent) => {
    const app = appRef.current
    if (!app) return
    const v = viewRef.current

    if (!(e.ctrlKey || e.metaKey)) {
      // Pan. Shift with a one-axis wheel is the old convention for sideways,
      // and a trackpad already sends deltaX on its own.
      const dx = e.shiftKey && !e.deltaX ? e.deltaY : e.deltaX
      const dy = e.shiftKey && !e.deltaX ? 0 : e.deltaY
      v.x -= dx
      v.y -= dy
      applyView()
      redraw()
      return
    }

    const rect = app.canvas.getBoundingClientRect()
    const mx = e.clientX - rect.left
    const my = e.clientY - rect.top
    const factor = Math.exp(-e.deltaY * 0.0015)
    const zoom = Math.min(12, Math.max(0.05, v.zoom * factor))
    // keep the point under the cursor fixed
    v.x = mx - ((mx - v.x) * zoom) / v.zoom
    v.y = my - ((my - v.y) * zoom) / v.zoom
    v.zoom = zoom
    applyView()
    onZoomChange?.(zoom)
    redraw()
  }

  /** Zoom about the center of the view, for the on-canvas buttons. */
  const zoomBy = useCallback(
    (factor: number) => {
      const app = appRef.current
      if (!app) return
      const host = hostRef.current
      const cw = host?.clientWidth ?? app.renderer.width
      const ch = host?.clientHeight ?? app.renderer.height
      const v = viewRef.current
      const zoom = Math.min(12, Math.max(0.05, v.zoom * factor))
      const mx = cw / 2
      const my = ch / 2
      v.x = mx - ((mx - v.x) * zoom) / v.zoom
      v.y = my - ((my - v.y) * zoom) / v.zoom
      v.zoom = zoom
      applyView()
      onZoomChange?.(zoom)
      redraw()
    },
    [applyView, redraw, onZoomChange],
  )

  /* ---------------- export ---------------- */

  const exportBlob = useCallback(async (scale = 1): Promise<Blob | null> => {
    const app = appRef.current
    const refs = refsRef.current
    const doc = useEditor.getState().doc
    if (!app || !refs || !doc.photo) return null

    const { width: W, height: H } = doc.photo
    const saved = { ...viewRef.current }
    const overlayVisible = refs.overlay.visible

    refs.overlay.visible = false
    refs.world.position.set(0, 0)
    refs.world.scale.set(1)

    let fx = 0
    let fy = 0
    let fw = W
    let fh = H
    if (doc.scene.crop169) {
      const target = 16 / 9
      fh = Math.min(H, W / target)
      fw = fh * target
      fx = (W - fw) / 2
      fy = (H - fh) / 2
    }

    let blob: Blob | null = null
    try {
      const canvas = app.renderer.extract.canvas({
        target: refs.world,
        frame: new Rectangle(fx, fy, fw, fh),
        resolution: scale,
      })
      blob = await new Promise<Blob | null>((resolve) => {
        if ('toBlob' in canvas && typeof canvas.toBlob === 'function') {
          canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.94)
        } else {
          resolve(null)
        }
      })
    } finally {
      refs.overlay.visible = overlayVisible
      viewRef.current = saved
      applyView()
      redraw()
    }
    return blob
  }, [applyView, redraw])

  useEffect(() => {
    onReady({ exportBlob, fit, zoomBy, night: () => nightRef.current })
  }, [exportBlob, fit, zoomBy, onReady])

  return (
    <div
      ref={hostRef}
      className="stage"
      /*
        Scroll pans the photo now, and a sideways scroll at the edge of a page
        is what macOS reads as a back swipe. Containing the overscroll stops
        the browser leaving the app mid drag, without a preventDefault that
        React may have made passive anyway.
      */
      style={{ overscrollBehavior: 'contain' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onDoubleClick={onDoubleClick}
      onWheel={onWheel}
    />
  )
}
